import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TriangleAlert, CheckCircle2, Loader2, ChevronRight, RefreshCw } from "lucide-react";

function ReconcileAllDialog({ open, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setPreview(null); return; }
    api.get("/admin/zones/orphan-totals")
      .then(({ data }) => setPreview(data))
      .catch((e) => { toast.error(apiError(e.response?.data?.detail)); onOpenChange(false); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = (preview?.items || []).filter((p) => p.orphans > 0 || p.mismatched > 0);
  const total = rows.reduce((s, p) => s + p.orphans + p.mismatched, 0);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/zones/reconcile-prospects", { dry_run: false });
      toast.success(`Re-homed ${data.applied} prospect${data.applied === 1 ? "" : "s"} across all paths`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reconcile-all-dialog">
        <DialogHeader>
          <DialogTitle>Reconcile all paths</DialogTitle>
          <DialogDescription>Moves every prospect to the live zone whose boundary contains it, on every bike path. This preview makes no changes.</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border p-3 text-sm flex items-center gap-2" data-testid="reconcile-all-clean"><CheckCircle2 className="w-4 h-4 text-emerald-400" />Everything is already correctly assigned — no changes needed.</div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm font-medium" data-testid="reconcile-all-total">{total} prospect{total === 1 ? "" : "s"} would be re-homed:</div>
            <div className="max-h-64 overflow-y-auto divide-y divide-border rounded-lg border border-border">
              {rows.map((p) => (
                <div key={p.bikePathId} className="px-3 py-2 flex items-center justify-between gap-3 text-sm" data-testid={`reconcile-all-row-${p.bikePathId}`}>
                  <span className="truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{p.orphans} from deleted · {p.mismatched} from other zones</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="reconcile-all-close">Close</Button>
          <Button onClick={apply} disabled={busy || !rows.length} data-testid="reconcile-all-apply">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Apply fix to all</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OrphanProspectsWidget({ onOpenPath }) {
  const [data, setData] = useState(null);
  const [allOpen, setAllOpen] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    api.get("/admin/zones/orphan-totals")
      .then(({ data }) => { if (live) setData(data); })
      .catch(() => { if (live) setData({ totalOrphans: 0, items: [], error: true }); });
    return () => { live = false; };
  }, [nonce]);

  const total = data?.totalOrphans || 0;
  return (
    <div className={`rounded-2xl border bg-card p-5 ${total > 0 ? "border-red-500/40" : "border-border"}`} data-testid="orphan-totals-widget">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${total > 0 ? "bg-red-500/10" : "bg-emerald-500/10"}`}>
          {total > 0 ? <TriangleAlert className="w-4 h-4 text-red-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
        </div>
        <div>
          <h3 className="font-heading font-bold">Orphan prospects by bike path</h3>
          <p className="text-xs text-muted-foreground" data-testid="orphan-totals-summary">
            {!data ? "Checking…" : data.error ? "Couldn't load orphan totals." : total > 0 ? `${total} prospect${total === 1 ? " is" : "s are"} still linked to deleted zones. Click a path to reconcile.` : "No orphaned prospects — every prospect is linked to a live zone."}
          </p>
        </div>
        {total > 0 && <Button size="sm" variant="outline" className="ml-auto border-red-500/50 text-red-500" onClick={() => setAllOpen(true)} data-testid="reconcile-all-button"><RefreshCw className="w-4 h-4 mr-1" />Reconcile all paths</Button>}
      </div>
      <ReconcileAllDialog open={allOpen} onOpenChange={setAllOpen} onDone={() => setNonce((n) => n + 1)} />
      {!data ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : (
        <div className="divide-y divide-border max-h-80 overflow-y-auto" data-testid="orphan-totals-list">
          {data.items.map((p) => (
            <button key={p.bikePathId} type="button" onClick={() => onOpenPath(p.bikePathId)} className="w-full py-2.5 flex items-center gap-3 text-left hover:bg-muted/40 transition-colors rounded-md px-2" data-testid={`orphan-path-${p.bikePathId}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.name || "Bike path"}</div>
                {p.mismatched > 0 && <div className="text-xs text-amber-600">{p.mismatched} assigned to another zone</div>}
              </div>
              <span className={`rounded-md px-2 py-0.5 text-xs font-bold tabular-nums border ${p.orphans > 0 ? "bg-red-500/10 text-red-500 border-red-500/30" : "bg-muted text-muted-foreground border-border"}`} data-testid={`orphan-count-${p.bikePathId}`}>
                {p.orphans} orphan{p.orphans === 1 ? "" : "s"}{p.zones > 0 ? ` · ${p.zones} zone${p.zones === 1 ? "" : "s"}` : ""}
              </span>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
